import { Injectable } from '@angular/core';
import { environment } from '../../environments';

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError as _throw } from 'rxjs';
import { map, catchError, debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ThirdpartyFilter } from '../models/thirdparty-filter.model';
import { ThirdPartyDetailItem } from '../models/thirdparty-detail-item';
import { ThirdPartyEvaluationStage } from '../models/thirdparty-evaluation-stage';
import { ThirdPartyRiskLevel } from '../models/thirdparty-risklevel';
import { ThirdPartyStatus } from '../models/thirdparty-status';
import { ThirdPartyIdentification } from '../models/thirdparty-identification';
import { ThirdPartyListItem } from '../models/thirdparty-listitem';
import { ThirdPartyEvaluationStageDto } from '../models/thirdparty-evaluation-stage-dto';
import { LocationCountry } from '../models/location-country';
import { ThirdPartyType } from '../models/thirdparty-type';
import { Culture } from '../models/culture/culture.table';
import { AuthService } from '../core/auth/_services/auth.service';
import { FileItem } from '../models/file-item';
import { ThirdPartyFrontEndDto } from '../models/thirdparty-create-dto';
import { ImportFileResult } from '../views/models/file-upload-button/model/IFileManagerService';

@Injectable({
  providedIn: 'root'
})
export class ThirdpartiesService {

  private apiEndPoint: string;

  constructor(
    private _http: HttpClient,
    private _translate: TranslateService,
    private _auth: AuthService,
  ) {
    this.apiEndPoint = environment.apiUrl;
  }

  getPaged(params: ThirdpartyFilter): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/ThirdParty/GetPagedByFilter`, params)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getStages(): Observable<ThirdPartyEvaluationStage[]> {
    return this._http
      .get<ThirdPartyEvaluationStageDto[]>(`${this.apiEndPoint}/ThirdParty/GetEvaluationStages`)
      .pipe(
        map(res => {
          return res.map(x => {
            var item = new ThirdPartyEvaluationStage();
            item.id = x.id;
            item.stageId = x.id;
            item.name = x.description;
            return item;
          });
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }


  exportToExcel(params: ThirdpartyFilter): Observable<any> {
    const culture: Culture = Culture[this._auth.currentUserLanguage];
    params.CultureName = culture;

    return this._http
      .post(`${this.apiEndPoint}/ThirdParty/GetThirdPartyListExcel`, params, { responseType: 'blob' })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getDetail(id: any): Observable<ThirdPartyDetailItem> {
    const params = new HttpParams()
      .append("thirdPartyId", id);

    return this._http
      .get<ThirdPartyDetailItem>(`${this.apiEndPoint}/ThirdParty/GetDetailOfThirdParty`, { params })
      .pipe(
        map(response => {
          return Object.assign(new ThirdPartyDetailItem(), response) as ThirdPartyDetailItem;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getWorkFlowSteps(thirdPartyTypeId: number, thirdPartyEvaluationId: number): Observable<ThirdPartyEvaluationStage[]> {
    let params = new HttpParams()
      .append("thirdPartyTypeId", thirdPartyTypeId.toString());;
    if (thirdPartyEvaluationId != null)
      params = params.append("thirdPartyEvaluationId", thirdPartyEvaluationId.toString());

    return this._http
      .get<ThirdPartyEvaluationStage[]>(`${this.apiEndPoint}/ThirdParty/GetWorkFlowSteps`, { params })
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getWorkFlowStepsAvailableForRestart(thirdPartyId: number): Observable<ThirdPartyEvaluationStage[]> {
    const params = new HttpParams()
      .append('thirdPartyId', thirdPartyId.toString());

    return this._http
      .get<ThirdPartyEvaluationStage[]>(`${this.apiEndPoint}/ThirdParty/GetWorkFlowsForEdit`, { params })
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRiskLevels(): Observable<ThirdPartyRiskLevel[]> {
    return this._http
      .get<ThirdPartyRiskLevel[]>(`${this.apiEndPoint}/ThirdParty/GeTLevels`)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getAvailableStatus(): Observable<ThirdPartyStatus[]> {
    return this._http
      .get<ThirdPartyStatus[]>(`${this.apiEndPoint}/ThirdParty/GetStatuses`)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getThirdPartyReplyStatus(): Observable<ThirdPartyStatus[]> {
    //ToDo #sbaleani: Change to right endpoint, recheck if this is the right one
    return this._http
      .get<ThirdPartyStatus[]>(`${this.apiEndPoint}/ThirdParty/GetStatuses`)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getIdentificatonTypes(): Observable<ThirdPartyIdentification[]> {
    return this._http
      .get<ThirdPartyIdentification[]>(`${this.apiEndPoint}/ThirdParty/GetTypeIds`)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  updateContactEmail(thirdPartyId: number, contactEmail: string): Observable<any> {
    const params = {
      thirdPartyId: thirdPartyId,
      emailToChange: contactEmail
    }

    return this._http
      .post(`${this.apiEndPoint}/ThirdParty/MoifyContactEmailThirdParty`, params)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getLocationCountries(): Observable<LocationCountry[]> {
    // ToDo #sbaleani: Replace with correct endpoint, Chek if this is the right one
    return this._http
      .get<LocationCountry[]>(`${this.apiEndPoint}/DashBoard/GetCountries`)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getTypes(): Observable<ThirdPartyType[]> {
    return this._http
      .get<ThirdPartyType[]>(`${this.apiEndPoint}/ThirdParty/GetAllThirdPartyTypes`)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getThirdPartyDisclaimer(thirdPartyId: number): Observable<string> {
    const params = new HttpParams()
      .append("thirdPartyId", thirdPartyId.toString());

    return this._http
      .get<string>(`${this.apiEndPoint}/ThirdParty/GetThirdPartyDisclaimer`, { params })
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  downloadTemplate(): Observable<any> {
    const culture: Culture = Culture[this._auth.currentUserLanguage];
    const params = new HttpParams()
      .append('cultureEnum', culture.toString());

    return this._http
      .get(`${this.apiEndPoint}/ThirdParty/DownloadTemplateFrontEndClient`, {
        params: params,
        responseType: 'blob'
      })
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  postFile(file: FileItem): Observable<ImportFileResult> {
    const culture: Culture = Culture[this._auth.currentUserLanguage];
    file.culture = culture;

    return this._http
      .post<ImportFileResult>(`${this.apiEndPoint}/ThirdParty/ImportExcelFrontEndClient`, file)
      .pipe(
        catchError(err => {
          err = err.error || err;
          if (err.ErrorCode) {
            return _throw(err.ErrorCode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        }),
      );
  }

  getThirdparty(id: number): Observable<ThirdPartyFrontEndDto> {
    return this._http
    .get<ThirdPartyFrontEndDto>(`${this.apiEndPoint}/ThirdParty?thirdPartyId=${id}`)
    .pipe(
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      }),
    );
  }

  createItem(item: ThirdPartyFrontEndDto): Observable<any> {
    return this._http.post(`${this.apiEndPoint}/ThirdParty`, item);
  }
  editItem(item: ThirdPartyFrontEndDto): Observable<any> {
    return this._http.put(`${this.apiEndPoint}/ThirdParty`, item);
  }
}
